import { useRef, useState } from 'react';
import { api, PHOTO_KIND_LABELS, type PhotoKind } from '../api.ts';
import { prepareImage } from '../lib/image.ts';
import { Icon } from './Icon.tsx';

/**
 * Загрузка снимков. Два входа — камера и галерея: на телефоне «снять сейчас»
 * и «взять снятое раньше» это разные сценарии, и прятать один за другим
 * значит заставлять выбирать в диалоге системы.
 */
export function PhotoUploader({
  specimenId,
  onUploaded,
}: {
  specimenId: string;
  onUploaded: () => void;
}) {
  const cameraInput = useRef<HTMLInputElement>(null);
  const galleryInput = useRef<HTMLInputElement>(null);
  const [kind, setKind] = useState<PhotoKind>('specimen');
  const [progress, setProgress] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function upload(files: FileList | null) {
    if (!files?.length) return;
    setError(null);

    for (const [index, file] of Array.from(files).entries()) {
      try {
        setProgress(
          files.length > 1 ? `Снимок ${index + 1} из ${files.length}…` : 'Готовим снимок…',
        );
        const prepared = await prepareImage(file);

        setProgress('Отправляем…');
        const body = new FormData();
        body.append('full', prepared.full, 'full.webp');
        body.append('thumb', prepared.thumb, 'thumb.webp');
        body.append('kind', kind);
        body.append('width', String(prepared.width));
        body.append('height', String(prepared.height));
        if (prepared.takenAt) body.append('taken_at', prepared.takenAt);
        // Координаты снимка сохраняются навсегда, но наверх, к образцу,
        // ничего не подставляется автоматически: предложение появляется
        // в карточке и только по правилам ТЗ 7.3 — вне домашней зоны и
        // пока координат нет ни у образца, ни у его выезда.
        if (prepared.latitude !== null) body.append('exif_latitude', String(prepared.latitude));
        if (prepared.longitude !== null) body.append('exif_longitude', String(prepared.longitude));

        await api.uploadPhoto(specimenId, body);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Не удалось загрузить снимок');
        break;
      }
    }

    setProgress(null);
    onUploaded();
  }

  return (
    <div className="uploader">
      <div className="label-key" style={{ marginTop: 0 }}>
        Тип снимка
      </div>
      <div className="chips">
        {(Object.keys(PHOTO_KIND_LABELS) as PhotoKind[]).map((value) => (
          <button
            key={value}
            type="button"
            className={kind === value ? 'chip' : 'chip chip--plain'}
            onClick={() => setKind(value)}
          >
            {PHOTO_KIND_LABELS[value]}
          </button>
        ))}
      </div>
      {kind === 'label' && (
        <div className="hint">
          Этикетка продавца показывается отдельным блоком, а не в общей галерее:
          это документ, а не иллюстрация
        </div>
      )}

      <div className="photo-row" style={{ marginTop: 'var(--space-3)' }}>
        <button
          type="button"
          className="well well--dashed"
          onClick={() => cameraInput.current?.click()}
          disabled={progress !== null}
        >
          <Icon name="camera" className="icon" />
          <span className="hint">снять</span>
        </button>
        <button
          type="button"
          className="well well--dashed"
          onClick={() => galleryInput.current?.click()}
          disabled={progress !== null}
        >
          <Icon name="photo" className="icon" />
          <span className="hint">из галереи</span>
        </button>
      </div>

      <input
        ref={cameraInput}
        type="file"
        accept="image/*"
        capture="environment"
        hidden
        onChange={(e) => {
          void upload(e.target.files);
          e.target.value = '';
        }}
      />
      <input
        ref={galleryInput}
        type="file"
        accept="image/*"
        multiple
        hidden
        onChange={(e) => {
          void upload(e.target.files);
          e.target.value = '';
        }}
      />

      {progress && <div className="hint">{progress}</div>}
      {error && (
        <div className="notice notice--danger" style={{ marginTop: 'var(--space-3)' }}>
          {error}
        </div>
      )}
    </div>
  );
}
